import { useCallback, useEffect, useMemo } from 'react'
import { GlobalEvents } from '@highlight/ipc'
import AuthIpcRenderer from '@renderer/ipc/AuthIpc.renderer'
import { useUser } from '@renderer/hooks/useUser'
import { disconnectDefaultProvider, checkGoogleCalendarConnected } from '@shared/utils/connections'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useGlobalState } from '@highlight/ipc/react'
import { atom, useAtom } from 'jotai'
import { useModals } from '@renderer/chat'
import { useConnections } from './use-connections'

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'loading'

export interface UseGoogleCalendarConnectionOptions {
  onConnect?: () => void
  onDisconnect?: () => void
  eventAction?: string
}

const initializedAtom = atom<boolean>(false)
const loadingAtom = atom<boolean>(false)
const isConnectingAtom = atom<boolean>(false)

/**
 * Hook for managing Google Calendar connection state and authentication flow
 *
 * Features:
 * - Manages Google OAuth connection flow
 * - Authentication state checking
 * - Visual states: disconnected, connecting, connected, loading
 * - Deep link callback handles connection confirmation (no polling needed)
 * - Global event integration for real-time updates
 * - Login prompt for unauthenticated users
 */
export function useGoogleCalendarConnection({
  onConnect,
  onDisconnect,
  eventAction = 'connect_google_calendar',
}: UseGoogleCalendarConnectionOptions = {}) {
  const [initialized, setInitialized] = useAtom(initializedAtom)
  const [connected, setConnected] = useGlobalState<boolean>('connected-google-calendar', false)
  const [loading, setLoading] = useAtom(loadingAtom)
  const [isConnecting, setIsConnecting] = useAtom(isConnectingAtom)
  const { openModal } = useModals()
  const user = useUser()

  // Use shared connections cache
  const { data: connections } = useConnections()

  const isDisabled = useMemo(() => {
    return loading || !initialized || isConnecting
  }, [loading, initialized, isConnecting])

  // Determine the current connection state
  const state: ConnectionState = useMemo(() => {
    if (!initialized) return 'loading'
    if (isConnecting || loading) return 'connecting'
    if (connected) return 'connected'
    return 'disconnected'
  }, [initialized, isConnecting, loading, connected])

  // Update connected state when connections data changes
  useEffect(() => {
    if (connections) {
      const connectionStatus = checkGoogleCalendarConnected(connections)
      setConnected(connectionStatus)
      setInitialized(true)
    }
  }, [connections, setConnected, setInitialized])

  // Handle connection initiation
  const handleConnect = useCallback(async () => {
    if (!user || user.isAnonymous || user.provider === 'supabase') {
      console.error('User is not logged in')
      openModal('login-prompt')
      return
    }

    setIsConnecting(true)

    // Track analytics event
    AmplitudeRenderer.trackEvent('Google Calendar Action', {
      action: eventAction,
      state: state,
    })

    const url = await AuthIpcRenderer.getConnectUrlForProvider('google')

    window.open(url, '_blank')
  }, [user, openModal, eventAction, state])

  // Handle disconnection
  const handleDisconnect = useCallback(async () => {
    setLoading(true)
    const token = await AuthIpcRenderer.getAccessToken()
    if (!token) {
      setLoading(false)
      return
    }

    try {
      await disconnectDefaultProvider('google', token)
      setLoading(false)
      setConnected(false)
      GlobalEvents.emit('connections.google.disconnect')
      onDisconnect?.()

      // Track analytics event
      AmplitudeRenderer.trackEvent('Google Calendar Action', {
        action: 'disconnect',
        state: 'connected',
      })
    } catch (error) {
      console.error('Failed to disconnect Google Calendar:', error)
      setLoading(false)
    }
  }, [onDisconnect, setLoading, setConnected])

  // Listen to global events for connection changes (from deep link callback)
  useEffect(() => {
    const handleConnectEvent = () => {
      setIsConnecting(false)
      onConnect?.()
    }

    const handleDisconnectEvent = () => {
      setConnected(false)
    }

    GlobalEvents.on('connections.google.connect', handleConnectEvent)
    GlobalEvents.on('connections.google.disconnect', handleDisconnectEvent)

    return () => {
      GlobalEvents.off('connections.google.connect', handleConnectEvent)
      GlobalEvents.off('connections.google.disconnect', handleDisconnectEvent)
    }
  }, [onConnect, setConnected])

  // Timeout connecting state after 2 minutes if no response
  useEffect(() => {
    if (!isConnecting) return

    const timeout = setTimeout(
      () => {
        setIsConnecting(false)
      },
      2 * 60 * 1000,
    )

    return () => clearTimeout(timeout)
  }, [isConnecting])

  return useMemo(() => {
    return {
      state,
      isDisabled,
      isConnected: connected,
      onConnect: handleConnect,
      onDisconnect: handleDisconnect,
      isLoading: loading || state === 'loading',
    }
  }, [state, isDisabled, connected, handleConnect, handleDisconnect, loading])
}
